import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  formatCurrencyV2,
  formatDate,
  formatYesNo,
  getKycChip,
} from "../../utils/helpers";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PaidIcon from "@mui/icons-material/Paid";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InfoIcon from "@mui/icons-material/Info";
import AssignmentIcon from "@mui/icons-material/Assignment";
import TimelineIcon from "@mui/icons-material/Timeline";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DownloadIcon from "@mui/icons-material/Download";
import {
  AccountBalanceWallet,
  ReceiptLong,
  Warning,
} from "@mui/icons-material";

const LoanProfile = ({ _data }) => {
  const [data] = useState(_data);
  const [openSections, setOpenSections] = useState({});
  const currentUser = useSelector((state) => state.auth.user);

  const isStaff = ["admin", "finance", "ccr"].includes(currentUser?.role);

  const toggleSection = (title) =>
    setOpenSections((prev) => ({ ...prev, [title]: !prev[title] }));

  const sections = [
    {
      title: "Loan Overview",
      icon: <AssignmentIcon color="primary" />,
      visible: true,
      fields: [
        { label: "Product ID", value: data.product_id },
        { label: "Installment Count", value: data.instalment_count },
        { label: "Start Date", value: formatDate(data.loan_start_date) },
        { label: "End Date", value: formatDate(data.loan_end_date) },
        { label: "Description", value: data.description },
      ],
    },
    {
      title: "Financial Details",
      icon: <PaidIcon color="success" />,
      visible: true,
      fields: [
        {
          label: "Principal Loan",
          value: formatCurrencyV2(data.loan_principal),
        },
        {
          label: "Amount Disbursed",
          value: formatCurrencyV2(data.amount_disburse),
        },
        { label: "Loan Interest", value: formatCurrencyV2(data.loan_interest) },
        {
          label: "Total Payable",
          value: formatCurrencyV2(data.instalment_payable),
        }, // Assuming this exists or derived
        { label: "Amount Paid", value: formatCurrencyV2(data.amount_paid) },
        {
          label: "Outstanding Balance",
          value: formatCurrencyV2(data.balance_payable), // Assuming this exists
        },
      ],
    },
    {
      title: "Fees & Charges",
      icon: <ReceiptLong color="secondary" />,
      visible: true,
      fields: [
        {
          label: "Application Fee",
          value: formatCurrencyV2(data.application_fee_amount),
        },
        {
          label: "Insurance Amount",
          value: formatCurrencyV2(data.insurance_amount),
        },
        { label: "Other Charges", value: formatCurrencyV2(data.other_charges) },
      ],
    },
    {
      title: "Schedule & Dates",
      icon: <AccessTimeIcon color="info" />,
      visible: true,
      fields: [
        {
          label: "Last Contractual Date",
          value: formatDate(data.last_contractual_instal_date),
        },
        {
          label: "Next Contractual Date",
          value: formatDate(data.next_contractual_instal_date),
        },
        {
          label: "Next Actual Date",
          value: formatDate(data.next_actual_instal_date),
        },
        {
          label: "Last Interest Date",
          value: formatDate(data.interest_last_date),
        },
      ],
    },
    {
      title: "Detailed Balances",
      icon: <AccountBalanceWallet color="warning" />,
      visible: isStaff,
      fields: [
        {
          label: "Principal Balance",
          value: formatCurrencyV2(data.principal_balance),
        },
        {
          label: "Principal Inst. Bal",
          value: formatCurrencyV2(data.principal_instalment_balance),
        },
        {
          label: "Interest Balance",
          value: formatCurrencyV2(data.loan_interest_bal),
        },
        {
          label: "App Fee Balance",
          value: formatCurrencyV2(data.application_fee_bal),
        },
        {
          label: "Insurance Balance",
          value: formatCurrencyV2(data.insurance_bal),
        },
        {
          label: "Other Charges Bal",
          value: formatCurrencyV2(data.other_charges_bal),
        },
      ],
    },
    {
      title: "Rollover Information",
      icon: <TimelineIcon color="error" />,
      visible: data.rollover === "YES" || data.rollover_times > 0,
      fields: [
        { label: "Rollover Active", value: formatYesNo(data.rollover) },
        { label: "Rollover Times", value: data.rollover_times },
        {
          label: "Rollover Interest",
          value: formatCurrencyV2(data.rollover_interest),
        },
        {
          label: "Rollover Int. Bal",
          value: formatCurrencyV2(data.rollover_interest_bal),
        },
        {
          label: "Start Date",
          value: formatDate(data.rollover_start_date),
        },
        {
          label: "End Date",
          value: formatDate(data.rollover_end_date),
        },
        {
          label: "Next Date",
          value: formatDate(data.rollover_next_date),
        },
      ],
    },
    {
      title: "Debt Collection",
      icon: <Warning color="error" />,
      visible:
        isStaff &&
        (data.debt_collection === "YES" || data.debt_collection_bal > 0),
      fields: [
        {
          label: "In Debt Collection",
          value: formatYesNo(data.debt_collection),
        },
        {
          label: "Collection Balance",
          value: formatCurrencyV2(data.debt_collection_bal),
        },
        {
          label: "Start Date",
          value: formatDate(data.debt_collection_start_date),
        },
        {
          label: "End Date",
          value: formatDate(data.debt_collection_end_date),
        },
      ],
    },
    {
      title: "Administrative Data",
      icon: <InfoIcon color="disabled" />,
      visible: isStaff,
      fields: [
        { label: "Loan ID", value: data.id },
        { label: "Entity ID", value: data.entity_id },
        { label: "Request ID", value: data.request_id },
        // { label: "Supplier ID", value: data.supplier_id },
        { label: "Created On", value: formatDate(data.start_date) },
      ],
    },
  ];

  return (
    <Box>
      {/* HEADER SECTION */}
      <Card
        sx={{
          mb: 4,
          p: 1,
          borderRadius: 3,
          // background: "linear-gradient(135deg, #f3f6f9 0%, #ffffff 100%)",
          background: "#f3f6f9",
          boxShadow: 2,
        }}
      >
        <CardHeader
          title={<Typography variant="h6">Loan Details</Typography>}
          action={getKycChip(data.active_status)}
        />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">
                Loan Name
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {data.loan_name || "N/A"}
              </Typography>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">
                Loan ID
              </Typography>
              <Typography variant="body1">{data.id}</Typography>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">
                Start Date
              </Typography>
              <Typography variant="body1">
                {formatDate(data.loan_start_date)}
              </Typography>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              {/* {getKycChip(data.active_status)} */}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* SUMMARY CARDS */}
      <Grid container spacing={2} mb={4}>
        {[
          {
            label: "Principal Loan",
            value: formatCurrencyV2(data.loan_principal),
            color: "#4caf50",
            icon: <PaidIcon color="success" />,
          },
          {
            label: "Outstanding Balance",
            value: formatCurrencyV2(
              data.principal_balance + data.loan_interest_bal,
            ), // Approximate total balance
            color: "#ff9800",
            icon: <TimelineIcon color="warning" />,
          },
          {
            label: "Amount Paid",
            value: formatCurrencyV2(data.amount_paid),
            color: "#2196f3",
            icon: <AssignmentIcon color="primary" />,
          },
          {
            label: "Next Installment",
            value: formatDate(data.next_contractual_instal_date),
            color: "#9c27b0",
            icon: <AccessTimeIcon color="secondary" />,
          },
        ].map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.label}>
            <Card
              sx={{
                borderTop: `4px solid ${item.color}`,
                borderRadius: 3,
                backgroundColor: "#fff",
                height: "100%",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      color="text.secondary"
                      variant="subtitle2"
                      gutterBottom
                    >
                      {item.label}
                    </Typography>
                    <Typography variant="h6" fontWeight="700">
                      {item.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: `${item.color}15`, // Light background
                    }}
                  >
                    {item.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* DETAILED SECTIONS */}
      <Grid container spacing={3}>
        {sections
          .filter((section) => section.visible)
          .map((section) => (
            <Grid item xs={12} md={6} key={section.title}>
              <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                <CardHeader
                  title={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {section.icon}
                      <Typography variant="h6" fontWeight="bold">
                        {section.title}
                      </Typography>
                    </Stack>
                  }
                  action={
                    <IconButton onClick={() => toggleSection(section.title)}>
                      {openSections[section.title] === false ? (
                        <ExpandMoreIcon />
                      ) : (
                        <ExpandLessIcon />
                      )}
                    </IconButton>
                  }
                />
                <Collapse in={openSections[section.title] ?? true}>
                  <Divider />
                  <CardContent>
                    <Grid container spacing={2}>
                      {section.fields.map((field, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            {field.label}
                          </Typography>
                          <Typography variant="subtitle1">
                            {field.value || "—"}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Collapse>
              </Card>
            </Grid>
          ))}
      </Grid>
    </Box>
  );
};

export default LoanProfile;
